import { createContext, useContext, useRef, type ReactNode } from 'react';
import { T, useGT, useLocale } from 'gt-react';
import { InfoIcon, WarningCircleIcon } from '@phosphor-icons/react';
import type { ReportComponentNote, ReportDateRange } from '@erp/shared-types';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PERSISTENT_SCROLLBAR } from '@/lib/persistentScrollbar';
import { cn } from '@/lib/utils';
import { ConfigureAffordanceContext } from './charts/shared';
import { useReportContentRows } from './contentFit';
import {
  datasetTextContext,
  formatReportText,
  templateValue,
  type TextContext,
} from './format/reportText';
import {
  useComponentDataset,
  useComponentTextDataset,
  type ReportDataset,
} from './ReportDataContext';
import { ReportLink } from './ReportLink';
import type { ReportComponentConfig } from './types';

// ── The frame around a component ─────────────────────────────────────────────
// Every component type gets the same frame: its title (with `{placeholders}`
// of its dataset), a pinned-range badge, a help icon, a description, notes
// above and below its body and a link at its foot, and, for a component with
// its own `source`, its data's loading, error and empty states. A component
// that sets none of these, on a surface that doesn't ask for cards, keeps
// the canvas's plain title line.

/**
 * Set by a surface whose components each read as a card of their own (a
 * dashboard). While editing, the canvas cell is already the card.
 */
export const ReportCardChromeContext = createContext(false);

/** True inside a framed component: the frame pads its body, so renderers add none. */
const ComponentFramedContext = createContext(false);

export function useIsFramed(): boolean {
  return useContext(ComponentFramedContext);
}

/** The frame-only fields a component may set. */
function hasFrameFields(component: ReportComponentConfig): boolean {
  return Boolean(
    component.description ||
    component.tooltip ||
    component.notes?.length ||
    component.link ||
    component.source,
  );
}

/** Whether a component draws in the frame rather than under a plain title line. */
export function useFramed(component: ReportComponentConfig): boolean {
  const card = useContext(ReportCardChromeContext);
  return card || hasFrameFields(component);
}

/** What `{placeholders}` in a component's text read: its datasets' values. */
export function useComponentTextContext(
  component: ReportComponentConfig,
): TextContext {
  const locale = useLocale();
  const dataset = useComponentDataset(component);
  const textDataset = useComponentTextDataset(component);
  return datasetTextContext(dataset, textDataset, locale);
}

/** True while the canvas is being edited. */
function useIsEditing(): boolean {
  return useContext(ConfigureAffordanceContext) !== 'none';
}

/** A short label for a pinned range, e.g. "Last 30 days". */
export function usePinnedRangeLabel(range: ReportDateRange | undefined) {
  const gt = useGT();
  if (!range) return null;
  const context = 'badge on a report widget pinned to its own date range';
  if (range.kind === 'last_days')
    return gt('Last {days} days', { days: range.days, context });
  return gt('{from} to {to}', { from: range.from, to: range.to, context });
}

function PinnedRangeBadge({ range }: { range: ReportDateRange | undefined }) {
  const label = usePinnedRangeLabel(range);
  if (!label) return null;
  return (
    <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      {label}
    </span>
  );
}

function HelpIcon({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <InfoIcon
          className="h-3.5 w-3.5 shrink-0 cursor-help text-muted-foreground/60"
          aria-label={text}
        />
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-64">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function noteShown(note: ReportComponentNote, context: TextContext): boolean {
  if (!note.when) return true;
  const value = templateValue(note.when.value, context);
  switch (note.when.is) {
    case 'positive':
      return value != null && value > 0;
    case 'zero':
      return value === 0;
    case 'present':
      return value != null;
    case 'absent':
      return value == null;
  }
}

function Notes({
  notes,
  placement,
  context,
}: {
  notes: readonly ReportComponentNote[] | undefined;
  placement: 'above' | 'below';
  context: TextContext;
}) {
  const shown = (notes ?? []).filter(
    (note) =>
      (note.placement ?? 'below') === placement && noteShown(note, context),
  );
  if (shown.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {shown.map((note, index) => (
        <p
          key={index}
          className={cn(
            'text-xs',
            placement === 'below' && 'text-muted-foreground',
          )}
        >
          {formatReportText(note.text, context)}
        </p>
      ))}
    </div>
  );
}

function ProblemState({
  heading,
  lines,
}: {
  heading: ReactNode;
  lines: string[];
}) {
  return (
    <div
      role="alert"
      className="flex flex-1 flex-col items-center justify-center gap-1 p-3 text-center"
    >
      <WarningCircleIcon className="h-5 w-5 text-amber-500" />
      <p className="text-xs font-medium">{heading}</p>
      {lines.slice(0, 4).map((line, index) => (
        <p
          key={index}
          className="max-w-full break-words font-mono text-[11px] text-muted-foreground"
        >
          {line}
        </p>
      ))}
    </div>
  );
}

/**
 * What stands in for a sourced component's body until its data can be
 * drawn: a skeleton while it loads, the reason it can't be answered, or its
 * empty text. Null once there are rows to draw.
 */
function sourceState(
  component: ReportComponentConfig,
  dataset: ReportDataset | null,
): ReactNode {
  const error = dataset?.error;
  if (error)
    return (
      <ProblemState
        heading={
          error.code ? (
            <T>This widget's query can't be answered</T>
          ) : (
            <T>This widget's data didn't load</T>
          )
        }
        lines={[error.code ? `${error.code}: ${error.message}` : error.message]}
      />
    );
  if (!dataset || (dataset.isLoading && dataset.rows.length === 0))
    return (
      <div className="flex flex-1 flex-col gap-2" aria-busy>
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="min-h-12 w-full flex-1" />
      </div>
    );
  if (dataset.rows.length === 0)
    return (
      <p className="text-xs text-muted-foreground">
        {component.emptyText ?? (
          <T context="report widget whose data has no rows">Nothing to show.</T>
        )}
      </p>
    );
  return null;
}

/** Fills the cell and reports the grid rows the component's content needs. */
function Measured({ id, children }: { id: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useReportContentRows(id, ref);
  return (
    <div ref={ref} className="flex h-full min-h-0 flex-col">
      {children}
    </div>
  );
}

/**
 * A component in its frame. `body` is the component's renderer; `title` the
 * title it shows (its own or its type's default), null for none.
 */
export function ComponentFrame({
  component,
  title,
  rendersOwnTitle,
  body,
}: {
  component: ReportComponentConfig;
  title: string | null;
  rendersOwnTitle: boolean;
  body: ReactNode;
}) {
  const card = useContext(ReportCardChromeContext);
  const editing = useIsEditing();
  const dataset = useComponentDataset(component);
  const context = useComponentTextContext(component);
  const state = component.source ? sourceState(component, dataset) : null;
  const showTitle = title != null && !rendersOwnTitle;
  const shell = cn(
    'flex h-full min-h-0 flex-col gap-0 p-0',
    (!card || editing) && 'rounded-none border-0 bg-transparent shadow-none',
  );
  const content = (
    <>
      {(showTitle || component.description || component.source?.range) && (
        <div className="flex flex-col gap-1 p-3 pb-1">
          <div
            className={cn(
              'flex items-start justify-between gap-2',
              // Room for the canvas's hover toolbar while editing.
              editing && 'pr-16',
            )}
          >
            <div className="flex min-w-0 items-center gap-1">
              {showTitle && (
                <p
                  className="text-sm font-medium leading-snug"
                  data-component-title
                >
                  {formatReportText(title, context)}
                </p>
              )}
              {component.tooltip && (
                <HelpIcon text={formatReportText(component.tooltip, context)} />
              )}
            </div>
            <PinnedRangeBadge range={component.source?.range} />
          </div>
          {component.description && (
            <p className="text-xs text-muted-foreground">
              {formatReportText(component.description, context)}
            </p>
          )}
        </div>
      )}
      <div
        data-frame-body
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-3 pt-2',
          PERSISTENT_SCROLLBAR,
        )}
      >
        <Notes notes={component.notes} placement="above" context={context} />
        {state ?? body}
        <Notes notes={component.notes} placement="below" context={context} />
        {component.link && (
          <ReportLink
            href={component.link.href}
            className="self-start text-xs text-muted-foreground hover:underline"
          >
            {component.link.label}
          </ReportLink>
        )}
      </div>
    </>
  );
  return (
    <Measured id={component.id}>
      <ComponentFramedContext.Provider value>
        <Card className={shell}>{content}</Card>
      </ComponentFramedContext.Provider>
    </Measured>
  );
}
